import { Component, EventEmitter, HostListener, Output, ViewChild, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { TestContentDirective } from '../../directives/test-content.directive';
import { Navigation2Service } from '../../services/navigation2.service';
import { DesignComponent } from '../design/design.component';
import { View } from 'src/app/interfaces/view';
import { FormComponent } from '../form/form.component';
import { MaterialSelectComponent } from '../material-select/material-select.component';
import { WidthComponent } from '../width/width.component';
import { SizeComponent } from '../size/size.component';
import { AttributeMenuComponent } from '../attribute-menu/attribute-menu.component';
import { CheckoutSummaryComponent } from '../checkout-summary/checkout-summary.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { CheckoutService } from '../../services/checkout.service';
import { RetailerSummaryComponent } from '../retailer-summary/retailer-summary.component';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { GemstonesComponent } from '../gemstones/gemstones/gemstones.component';
import { EngravingComponent } from '../engraving/engraving.component';
import { assert } from 'src/app/utils/assert.util';
import { ViewTitleButton } from 'src/app/interfaces/view-title-button';
import { TopBarService } from 'src/app/services/top-bar-service/top-bar-service.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-customizer-panel2',
  templateUrl: './customizer-panel2.component.html',
  styleUrls: ['./customizer-panel2.component.scss']
})
export class CustomizerPanel2Component {
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private navigationService = inject(Navigation2Service);
  private checkoutService = inject(CheckoutService);
  private topBarService = inject(TopBarService);
  @Output() selectedAttributesChange = new EventEmitter<Map<string, string | null>>();
  @ViewChild(TestContentDirective, { static: true }) testContent!: TestContentDirective;
  canRetailerAddToCart = input(false);
  viewStack: any[] = [];
  indexedViews: View[] = [{ title: 'Design', type: DesignComponent, doesAppearInProgressBar: true }, { title: 'Profile', type: FormComponent, doesAppearInProgressBar: true }, { title: 'Edge Material', type: MaterialSelectComponent, doesAppearInProgressBar: true }, { title: 'Center Material', type: MaterialSelectComponent, doesAppearInProgressBar: true }, { title: 'Width', type: WidthComponent, doesAppearInProgressBar: true }, { title: 'Finger Size', type: SizeComponent, doesAppearInProgressBar: true }, { title: 'Gemstones', type: GemstonesComponent, doesAppearInProgressBar: true }, { title: 'Engraving', type: EngravingComponent, doesAppearInProgressBar: true }];
  progressBarViews: View[] = [];
  currentTitle = signal<string | null>(null);

  shouldShowBackButton = false;
  backButtonAnimationDirection: 'In' | 'Out' | null = null;
  shouldShowNextButton = false;
  nextButtonAnimationDirection: 'In' | 'Out' | null = null;
  shouldButtonsAnimate = false;
  isMenuOpen = false;
  canNavigateNext = computed(() => this.navigationService.canNavigateNext());
  canNavigateBack = computed(() => this.navigationService.canNavigateBack());
  activeView = computed(() => this.navigationService.activeView());
  isActiveViewTitleless = computed(() => typeof this.activeView().shouldHideTitle !== 'undefined' && this.activeView().shouldHideTitle);
  overrideTitle = computed(() => this.navigationService.overrideTitle());
  windowWidth = signal<number | null>(null);
  leftTitleButtons = computed(() => this.navigationService.globalTitleButtons().filter(b => b.location === 'left').concat(this.navigationService.titleButtons().filter(b => b.location === 'left')));
  rightTitleButtons = computed(() => this.navigationService.globalTitleButtons().filter(b => b.location === 'right').concat(this.navigationService.titleButtons().filter(b => b.location === 'right')));
  shouldShowNavBar = computed(() => this.navigationService.shouldShowNavBar());
  subtitle = computed(() => this.navigationService.subtitle());
  activeBarItem = computed(() => this.topBarService.activeItem());
  subscriptions: Subscription[] = [];

  constructor() {
    effect(() => {
      if (this.navigationService.canNavigateBack()) {
        this.shouldShowBackButton = true;
        this.backButtonAnimationDirection = this.shouldButtonsAnimate ? 'In' : null;
      } else {
        this.backButtonAnimationDirection = this.shouldButtonsAnimate ? 'Out' : null;
      }
    });

    effect(() => {
      if (this.navigationService.canNavigateNext()) {
        this.shouldShowNextButton = true;
        this.nextButtonAnimationDirection = this.shouldButtonsAnimate ? 'In' : null;
      } else {
        this.nextButtonAnimationDirection = this.shouldButtonsAnimate ? 'Out' : null;
      }
    });

    effect(() => {
      const activeView = this.navigationService.activeView();
      if (activeView) {
        this.currentTitle.set(activeView.displayTitle ?? activeView.title);
        this.testContent.viewContainerRef.clear();
        const ref = this.addView(activeView);
        if (this.viewStack.length === 0) {
          this.viewStack.push({ref, activeView});
        } else {
          this.viewStack[0] = {ref, activeView};
        }
      }
    }, {allowSignalWrites: true});

    toObservable(this.navigationService.views).subscribe(() => {
      const newTitle = this.navigationService.activeView().displayTitle ?? this.navigationService.activeView().title;
      if (this.currentTitle() !== newTitle) {
        this.currentTitle.set(newTitle);
      }
    });
  }

  ngOnInit() {
    this.windowWidth.set(window.innerWidth);

    if (this.checkoutService.b2bData.retailPricingOnly) {
      this.indexedViews.push({ title: 'Retailer Summary', displayTitle: 'Summary', type: RetailerSummaryComponent, doesAppearInProgressBar: false, isHidden: true });
    }

    this.subscriptions.push(this.navigationService.subscribe((action) => {
      if(action.data.title && action.data.title === 'Summary') {
        if (action.action === 'Present') {
          this.isMenuOpen = true;
        } else if (action.action === 'Dismiss') {
          this.isMenuOpen = false;
        }
      }
    }));

    this.subscriptions.push(this.navigationService.subscribe((action) => {
      if (action.action === 'Present') {
        this.testContent.viewContainerRef.clear();
        const ref = this.addView(action.data);
        this.viewStack.push({ ref, activeView: action.data });
      } else if (action.action === 'Dismiss') {
        this.testContent.viewContainerRef.clear();
        this.viewStack.pop();
        this.addView(this.viewStack[this.viewStack.length - 1].activeView);
      }
    }));

    this.progressBarViews = this.indexedViews.filter(v => v.doesAppearInProgressBar);
    this.navigationService.views.set(this.indexedViews);
    this.navigationService.navigateTo(0);
    this.currentTitle.set(this.navigationService.activeView().displayTitle ?? this.navigationService.activeView().title);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
  }

  addView(view: View) {
    const ref = this.testContent.viewContainerRef.createComponent<any>(view.type);
    if (view.type === MaterialSelectComponent) {
      ref.instance.type = view.title === 'Edge Material' ? 'Primary' : 'Secondary';
    }
    return ref;
  }

  @HostListener('animationend', ['$event.target'])
  viewContainerAnimationEnd(target: EventTarget | null) {
    const castTarget = target as HTMLElement | null;
    assert(castTarget, 'No target');
    if (castTarget.classList.contains('BackButton')) {
      if (this.backButtonAnimationDirection === 'Out') {
        this.shouldShowBackButton = false;
      }
      this.backButtonAnimationDirection = null
    }

    if (castTarget.classList.contains('NextButton')) {
      if (this.nextButtonAnimationDirection === 'Out') {
        this.shouldShowNextButton = false;
      }
      this.nextButtonAnimationDirection = null
    }
  }

  @HostListener('window:resize', [])
  windowResize() {
    this.windowWidth.set(window.innerWidth);
  }

  prevClick() {
    this.triggerGAEvent('click', 'previous', '1');
    this.navigationService.back();
  }

  nextClick() {
    this.triggerGAEvent('click', 'next', '1');
    this.shouldButtonsAnimate = true;
    this.navigationService.next();
  }

  menuButtonClick() {
    this.triggerGAEvent('click', 'menu', '1');
    const summaryView = { title: 'Summary', type: AttributeMenuComponent, doesAppearInProgressBar: false };
    if (summaryView && !this.isMenuOpen) {
      this.navigationService.present(summaryView);
    } else {
      const viewIndex = this.navigationService.siblingViews.findIndex(sv => sv.title === summaryView?.title);
      if (viewIndex !== null) {
        this.navigationService.dismiss(viewIndex);
      }
    }
  }

  triggerGAEvent(action: string, title: string, value: string) {
    if(this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent(action, 'title_bar', title, value);
    }
  }

  getStyle(button: ViewTitleButton) {
    return button.style?.call(button);
  }

  getIconStyle(button: ViewTitleButton) {
    return button.iconStyle?.call(button);
  }
}
